<template>
  <v-container
    id="manualOrders"
    tag="section"
  >
    <base-material-card
      color="indigo"
      icon="mdi-cash-multiple"
      inline
      class="px-5 py-3"
      top-actions
    >
      <template v-slot:after-heading>
        <div class="display-2 font-weight-light">
          {{ $t('ManualOrders') }}
        </div>
      </template>

      <template v-slot:top-actions>
        <debounce-v-btn
          class="px-2 ml-1 v-btn--tight"
          color="info"
          @click="loadOrderLines()"
        >
          {{ $t('General.Buttons.Refresh') }}
        </debounce-v-btn>
      </template>

      <v-data-table
        class="row-action"
        :headers="headers"
        :hide-default-header="true"
        :hide-default-footer="true"
        :items="orderLines"
        :items-per-page="orderLinesCount"
        :loading="loadingOrderLines"
        :loading-text="$t('Pages.ManualOrders.Messages.OrderLinesLoadingMessage')"
      >
        <template v-slot:header="{ props: { headers } }">
          <thead>
            <tr>
              <th
                v-for="(header, i) in headers"
                :key="`header-${i}`"
                :class="(i + 1 === headers.length ? 'text-right' : '')"
              >
                {{ $t(header.text) }}
              </th>
            </tr>
          </thead>
        </template>
        <template v-slot:item="{ item }">
          <tr>
            <td>
              <v-btn
                class="px-2 ml-1"
                color="info"
                rounded
                small
                @click="gotoDetails(item)"
              >
                {{ $t('General.Buttons.Details') }}
              </v-btn>
              <v-btn
                v-if="item.ownedByName === null"
                class="px-2 ml-1"
                color="green"
                rounded
                small
                @click="showUpdateOrderLineDialog(item)"
              >
                {{ $t('General.Buttons.Start') }}
              </v-btn>
              <v-btn
                v-if="item.ownedByCurrentUser"
                class="px-2 ml-1"
                color="warning"
                rounded
                small
                @click="showUpdateOrderLineDialog(item)"
              >
                {{ $t('General.Buttons.Update') }}
              </v-btn>
              <v-btn
                v-if="!item.ownedByCurrentUser && item.ownedByName !== null"
                class="px-2 ml-1"
                color="info"
                disabled
                rounded
                small
              >
                {{ $t('General.Buttons.OwnedByEntity', [item.ownedByName]) }}
              </v-btn>
            </td>
            <td>{{ item.orderDate | moment('DD/MM/YYYY HH:mm:ss') }}</td>
            <td>{{ getResellerName(item.resellerId) }}</td>
            <td>{{ getCustomerName(item.customerId) }}</td>
            <td>{{ getProductName(item.productId) }}</td>
            <td>{{ getProductVariantNameByVariantId(item.variantId) }}</td>
            <td>{{ getProductVariantSkuByVariantId(item.variantId) }}</td>
            <td class="text-right">
              {{ item.quantity }}
            </td>
          </tr>
        </template>
      </v-data-table>
    </base-material-card>

    <v-dialog
      v-if="updateOrderLineDialog"
      v-model="updateOrderLineDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title>
          {{ $t('Pages.ManualOrders.Dialogs.UpdateOrderLine.Title') }}
          <v-spacer />
          <v-icon
            :aria-label="$t('General.Buttons.Close')"
            @click="cancelUpdateOrderLine()"
          >
            mdi-close
          </v-icon>
        </v-card-title>

        <v-card-text
          class="text-center"
        >
          {{ $t('Pages.ManualOrders.Dialogs.UpdateOrderLine.Message') }}
        </v-card-text>

        <v-card-text>
          <v-form ref="updateOrderLineForm">
            <v-container>
              <v-row>
                <v-col
                  cols="12"
                >
                  <v-text-field
                    v-model="updateOrderLineDialog.vendorReferenceIdentifier"
                    :label="$t('Pages.ManualOrders.Dialogs.UpdateOrderLine.Labels.VendorReferenceIdentifier')"
                  />
                </v-col>
                <v-col
                  cols="12"
                >
                  <v-text-field
                    v-model="updateOrderLineDialog.serialNumber"
                    :label="$t('Pages.ManualOrders.Dialogs.UpdateOrderLine.Labels.SerialNumber')"
                  />
                </v-col>
                <v-col
                  cols="12"
                >
                  <v-checkbox
                    v-model="updateOrderLineDialog.completed"
                    :label="$t('Pages.ManualOrders.Dialogs.UpdateOrderLine.Labels.Completed')"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-card-text class="pb-6 pt-12 text-center">
          <v-btn
            class="mr-3"
            text
            @click="cancelUpdateOrderLine()"
          >
            {{ $t('General.Buttons.Cancel') }}
          </v-btn>

          <debounce-v-btn
            color="success"
            text
            @click="updateOrderLine()"
          >
            {{ $t('Pages.ManualOrders.Dialogs.UpdateOrderLine.ConfirmButton') }}
          </debounce-v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import adminOrderService from '@/services/Admin/orderService'

  export default {
    data: () => ({
      headers: [
        {
          sortable: false,
          text: '',
          width: '1%',
        },
        {
          sortable: true,
          text: 'Pages.ManualOrders.TableHeaders.OrderDate',
        },
        {
          sortable: true,
          text: 'Pages.ManualOrders.TableHeaders.ResellerName',
        },
        {
          sortable: true,
          text: 'Pages.ManualOrders.TableHeaders.CustomerName',
        },
        {
          sortable: true,
          text: 'Pages.ManualOrders.TableHeaders.ProductName',
        },
        {
          sortable: true,
          text: 'Pages.ManualOrders.TableHeaders.VariantName',
        },
        {
          sortable: true,
          text: 'Pages.ManualOrders.TableHeaders.VariantSku',
        },
        {
          sortable: true,
          text: 'Pages.ManualOrders.TableHeaders.Quantity',
        },
      ],
      orderLines: [],
      orderLinesCount: 10,
      loadingOrderLines: false,
      updateOrderLineDialog: null,
    }),
    computed: {
      ...mapGetters(
        [
          'getCustomerName', 'getResellerName', 'getProductName',
          'getProductVariantNameByVariantId', 'getProductVariantSkuByVariantId',
        ]),
    },
    created: async function () {
      if (this.$store.state.resellers.list.length === 0) {
        await this.$store.dispatch('RefreshResellers')
      }
      if (this.$store.state.customers.data.length === 0) {
        await this.$store.dispatch('RefreshCustomers')
      }
      if (this.$store.state.products.list.length === 0) {
        await this.$store.dispatch('RefreshProducts')
      }
      this.loadOrderLines()
    },
    methods: {
      async loadOrderLines () {
        this.loadingOrderLines = true

        var orderLinesApiResult = await adminOrderService.getManualOrderLines()

        if (orderLinesApiResult.success === false) {
          this.loadingOrderLines = false
          return
        }

        this.orderLines = orderLinesApiResult.value.data
        this.orderLinesCount = this.orderLines.length
        this.loadingOrderLines = false
      },
      gotoDetails (item) {
        if (item.isSubscriptionQuantityUpdate) {
          this.$router.push({ name: 'Subscription', params: { uid: item.subscriptionUid } })
        } else {
          this.$router.push({ name: 'Order', params: { uid: item.orderUid } })
        }
      },
      showUpdateOrderLineDialog (orderLine) {
        this.updateOrderLineDialog = {
          orderLineId: orderLine.uid,
          vendorReferenceIdentifier: orderLine.vendorReferenceIdentifier || '',
          serialNumber: orderLine.serialNumber || '',
        }
      },
      async updateOrderLine () {
        this.loadingOrderLines = true

        await adminOrderService.updateOrderLine(this.updateOrderLineDialog)
        await this.loadOrderLines()

        this.loadingOrderLines = false
        this.updateOrderLineDialog = null
      },
      cancelUpdateOrderLine () {
        this.updateOrderLineDialog = null
      },
    },
  }
</script>
